import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Autocomplete,
  Card,
  CardHeader,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
} from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { heIL } from '@mui/x-data-grid/locales';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useUser } from '../contexts/UserContext';
import ResponsiveDataView from '../components/ResponsiveDataView';
import ResponsiveToolbar from '../components/ResponsiveToolbar';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestoreIcon from '@mui/icons-material/Restore';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useQueryClient } from '@tanstack/react-query';

// ==================== THEME ====================
const theme = createTheme({
  direction: 'rtl',
  ...heIL,
  typography: {
    fontSize: 16,
  },
});

// ==================== CUSTOM TOOLBAR ====================
function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
    </GridToolbarContainer>
  );
}

const DeletedClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { userProfile } = useUser();
  const role = userProfile?.role;

  // State for restore dialog
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [clientToRestore, setClientToRestore] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreError, setRestoreError] = useState('');
  const [restoreSuccess, setRestoreSuccess] = useState('');

  // State for permanent delete dialog
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deletePassword, setDeletePassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState('');

  const uiConfig = userProfile?.ui_config || {};
  const clientFieldsSchema = uiConfig.client_fields_schema || {};
  const ClientAllowedRoles = uiConfig.pages_configs?.ClientDetailPage?.allowed_roles || ["manager", "employee"];

  // Auto-clear success messages after 3 seconds
  useEffect(() => {
    if (restoreSuccess) {
      const timer = setTimeout(() => setRestoreSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [restoreSuccess]);

  useEffect(() => {
    if (deleteSuccess) {
      const timer = setTimeout(() => {
        setDeleteSuccess('');
        setPermanentDeleteDialogOpen(false);
        setClientToDelete(null);
        fetchDeletedClients();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [deleteSuccess]);

  // ============== FETCH DELETED CLIENTS ==============
  const fetchDeletedClients = async () => {
    setLoading(true);
    try {
      const response = await API.get('/clients/deleted/');
      console.log('Fetched deleted clients:', response.data);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching deleted clients:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeletedClients();
  }, []);

  // ============== SEARCH + FILTER ==============
  const filteredClients = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase().trim();
    if (!lowerSearch) return clients;
    return clients.filter((client) => {
      const combined = `${client.ClientName} (${client.ClientBusinessID})`.toLowerCase();
      return combined.includes(lowerSearch);
    });
  }, [clients, searchTerm]);

  // ==================== RESTORE CLIENT FUNCTIONS ====================
  const handleOpenRestoreDialog = (client) => {
    setClientToRestore(client);
    setRestoreDialogOpen(true);
    setRestoreError('');
    setRestoreSuccess('');
  };

  const handleCancelRestore = () => {
    setRestoreDialogOpen(false);
    setClientToRestore(null);
    setRestoreError('');
    setRestoreSuccess('');
  };

  const handleConfirmRestore = async () => {
    if (!clientToRestore) return;
    
    setRestoreLoading(true);
    setRestoreError('');
    setRestoreSuccess('');
    
    try {
      await API.post('/client/restore/', { 
        business_id: clientToRestore.ClientBusinessID 
      });
      
      setRestoreSuccess('הלקוח שוחזר בהצלחה!');
      
      // Invalidate the clients cache
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
      
      // Close dialog and refresh after a short delay
      setTimeout(() => {
        setRestoreDialogOpen(false);
        setClientToRestore(null);
        fetchDeletedClients();
      }, 1500);
    } catch (error) {
      console.error('Error restoring client:', error);
      setRestoreError(error?.response?.data?.error || 'שגיאה בשחזור הלקוח');
    } finally {
      setRestoreLoading(false);
    }
  };

  // ==================== PERMANENT DELETE FUNCTIONS ====================
  const handleOpenPermanentDeleteDialog = (client) => {
    setClientToDelete(client);
    setPermanentDeleteDialogOpen(true);
    setDeleteStep(1);
    setDeletePassword('');
    setDeleteError('');
    setDeleteSuccess('');
    setPasswordVisible(false);
  };

  const handleCancelPermanentDelete = () => {
    setPermanentDeleteDialogOpen(false);
    setClientToDelete(null);
    setDeleteStep(1);
    setDeletePassword('');
    setDeleteError('');
    setDeleteSuccess('');
    setPasswordVisible(false);
  };

  const handleProceedToStep2 = () => {
    setDeleteStep(2);
    setDeleteError('');
  };

  const handlePermanentDelete = async () => {
    if (!clientToDelete) return;
    
    setDeleteLoading(true);
    setDeleteError('');
    setDeleteSuccess('');
    
    try {
      await API.delete('/client/permanent-delete/', {
        data: {
          business_id: clientToDelete.ClientBusinessID,
          password: deletePassword
        }
      });
      
      setDeleteSuccess('הלקוח נמחק לצמיתות!');
      
      // Invalidate the clients cache (in case the list is open in another tab)
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
      
      // Auto-close will be handled by useEffect
    } catch (error) {
      console.error('Error permanently deleting client:', error);
      if (error.response?.status === 401) {
        setDeleteError('סיסמה שגויה');
      } else {
        setDeleteError(error?.response?.data?.error || 'שגיאה במחיקת הלקוח');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  // ============== DATA GRID COLUMNS ==============
  const columns = [
    {
      field: 'ClientName',
      headerName: clientFieldsSchema?.ClientName?.label || 'שם הלקוח',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params?.row || {};
        const canNavigateToClient = ClientAllowedRoles.includes(role);
        
        if (canNavigateToClient) {
          return (
            <Button
              variant="text"
              color="primary"
              onClick={() => navigate(`/client/${row.ClientBusinessID}`)}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              {row.ClientName}
            </Button>
          );
        } else {
          return (
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.primary',
                textAlign: 'center',
                fontSize: '1.1rem'
              }}
            >
              {row.ClientName}
            </Typography>
          );
        }
      },
    },
    {
      field: 'ClientBusinessID',
      headerName: 'תז/חפ',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'DeletedAt',
      headerName: 'נמחק בתאריך',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (!params.row.DeletedAt) return '-';
        const date = new Date(params.row.DeletedAt);
        return date.toLocaleDateString('he-IL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
    {
      field: 'actions',
      headerName: 'פעולות',
      flex: 1.5,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<RestoreIcon />}
              onClick={() => handleOpenRestoreDialog(params.row)}
              sx={{direction: "ltr"}}
            >
              שחזור
            </Button>
            {role === 'manager' && (
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<DeleteForeverIcon />}
                onClick={() => handleOpenPermanentDeleteDialog(params.row)}
                sx={{direction: "ltr"}}
              >
                מחיקה לצמיתות
              </Button>
            )}
          </Box>
        );
      },
    },
  ];

  // ==================== Mobile Card Renderer ====================
  const deletedClientCardRenderer = (row) => {
    const dateStr = row?.DeletedAt
      ? new Date(row.DeletedAt).toLocaleDateString('he-IL', {
          day: '2-digit', month: '2-digit', year: 'numeric',
        })
      : '';

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {row.ClientName}
          </Typography>
          <Typography variant="body2" color="text.secondary">{dateStr}</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          תז/חפ: {row.ClientBusinessID}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" color="success" size="small" onClick={() => handleOpenRestoreDialog(row)}>
            שחזור
          </Button>
          {role === 'manager' && (
            <Button variant="contained" color="error" size="small" onClick={() => handleOpenPermanentDeleteDialog(row)}>
              מחיקה
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <div dir="rtl">
        <Container maxWidth={false} sx={{ width: '100%', px: 2 }}>
          <Typography variant="h3" sx={{ mb: 3}}>
             לקוחות שנמחקו
          </Typography>

          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardHeader
              title={
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                  רשימת לקוחות שנמחקו
                </Typography>
              }
              sx={{
                backgroundColor: '#f44336', // Red color for deleted clients
                color: 'white',
                py: 1.5,
              }}
            />
            <CardContent>
              {/* Search row */}
              <ResponsiveToolbar>
                <Autocomplete
                  freeSolo
                  options={clients}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.ClientName
                      ? `${option.ClientName} (${option.ClientBusinessID})`
                      : '';
                  }}
                  filterOptions={(x) => x}  // Disable Autocomplete filtering (table does it)
                  loading={loading}
                  loadingText="טוען..."
                  noOptionsText="אין לקוחות מחוקים"
                  inputValue={searchTerm}
                  onInputChange={(event, newInputValue) => {
                    setSearchTerm(newInputValue);
                  }}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                      setSearchTerm(newValue);
                    } else if (newValue && typeof newValue === 'object') {
                      setSearchTerm(
                        `${newValue.ClientName} (${newValue.ClientBusinessID})`
                      );
                    } else {
                      setSearchTerm('');
                    }
                  }}
                  forcePopupIcon={false}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="חיפוש לפי שם או מזהה"
                      placeholder="הקלד שם או חפ/תז"
                      size="small"
                      sx={{ width: { xs: '100%', md: 250 } }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </ResponsiveToolbar>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    overflowX: 'auto',
                    maxHeight: 'calc(100vh - 250px)',
                    overflowY: 'auto',
                  }}
                >
                  <ResponsiveDataView
                    GridComponent={DataGrid}
                    rows={filteredClients}
                    columns={columns}
                    cardRenderer={deletedClientCardRenderer}
                    getRowId={(row) => row.ClientBusinessID}
                    emptyMessage="אין לקוחות מחוקים"
                    density="comfortable"
                    autoHeight
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{
                      pagination: {
                        paginationModel: { pageSize: 25, page: 0 },
                      },
                    }}
                    disableSelectionOnClick
                    localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                    slots={{
                      toolbar: CustomToolbar,
                    }}
                    components={{
                      NoRowsOverlay: () => (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            p: 4,
                          }}
                        >
                          אין לקוחות מחוקים
                        </Box>
                      ),
                    }}
                    sx={{
                      '.MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 'bold',
                        fontSize: '1.15rem',
                        whiteSpace: 'normal !important',
                        lineHeight: '1.2',
                        overflow: 'visible',
                      },
                      '.MuiDataGrid-cell': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        fontSize: '1rem',
                      },
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Container>

        {/* Restore Dialog */}
        <Dialog open={restoreDialogOpen} onClose={handleCancelRestore}>
          <DialogTitle>אישור שחזור לקוח</DialogTitle>
          <DialogContent>
            {restoreError && (
              <Alert severity="error" sx={{ mb: 2 }}>{restoreError}</Alert>
            )}
            {restoreSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>{restoreSuccess}</Alert>
            )}
            {!restoreSuccess && (
              <Typography>
                האם אתה בטוח שברצונך לשחזר את הלקוח {clientToRestore?.ClientName}?
              </Typography>
            )}
          </DialogContent>
          {!restoreSuccess && (
            <DialogActions>
              <Button onClick={handleCancelRestore}>ביטול</Button>
              <Button 
                onClick={handleConfirmRestore} 
                variant="contained" 
                color="success"
                disabled={restoreLoading}
              >
                {restoreLoading ? 'משחזר...' : 'שחזר'}
              </Button>
            </DialogActions>
          )}
        </Dialog>

        {/* Permanent Delete Dialog */}
        <Dialog 
          open={permanentDeleteDialogOpen} 
          onClose={deleteLoading ? undefined : handleCancelPermanentDelete}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ color: 'error.main' }}>
            {deleteStep === 1 ? 'אזהרה: מחיקה לצמיתות' : 'אישור סופי למחיקה לצמיתות'}
          </DialogTitle>
          <DialogContent>
            {deleteError && (
              <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>
            )}
            {deleteSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>{deleteSuccess}</Alert>
            )}
            
            {!deleteSuccess && deleteStep === 1 && (
              <>
                <Alert severity="error" sx={{ mb: 2 }}>
                  אזהרה: פעולה זו בלתי הפיכה!
                </Alert>
                <Typography sx={{ mb: 2 }}>
                  אתה עומד למחוק לצמיתות את הלקוח <strong>{clientToDelete?.ClientName}</strong>.
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  פעולה זו תמחק לצמיתות:
                </Typography>
                <ul>
                  <li>את רשומת הלקוח עצמה</li>
                  <li>את כל רשומות החוב של הלקוח</li>
                  <li>את כל התנועות של הלקוח</li>
                  <li>את כל החברויות בקבוצות</li>
                  <li>את כל הזמנות הלקוח </li>
                </ul>
              </>
            )}
            
            {!deleteSuccess && deleteStep === 2 && (
              <>
                <Typography sx={{ mb: 2 }}>
                  כדי לאשר את המחיקה הלצמיתות של <strong>{clientToDelete?.ClientName}</strong>, 
                  אנא הזן את הסיסמה שלך:
                </Typography>
                
                <TextField
                  label="סיסמה"
                  type={passwordVisible ? 'text' : 'password'}
                  fullWidth
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  sx={{ mb: 2, direction: 'ltr' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setPasswordVisible(!passwordVisible)}
                          edge="end"
                        >
                          {passwordVisible ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
          </DialogContent>
          {!deleteSuccess && (
            <DialogActions>
              <Button 
                onClick={handleCancelPermanentDelete} 
                disabled={deleteLoading}
              >
                ביטול
              </Button>
              {deleteStep === 1 ? (
                <Button 
                  onClick={handleProceedToStep2} 
                  variant="contained" 
                  color="error"
                >
                  המשך
                </Button>
              ) : (
                <Button 
                  onClick={handlePermanentDelete} 
                  variant="contained" 
                  color="error"
                  disabled={deleteLoading || !deletePassword}
                >
                  {deleteLoading ? 'מוחק לצמיתות...' : 'מחק לצמיתות'}
                </Button>
              )}
            </DialogActions>
          )}
        </Dialog>
      </div>
    </ThemeProvider>
  );
};

export default DeletedClientsPage;